// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { RootAdvisoryRepositoryState } from '@shared/data-access/interfaces/redux/advisory-repository-state-types';
import { createTypedSelector } from '@shared/data-access/utils';
import { isUndefined } from '@shared/utils';
import {
  advisoryPositionsAdapter,
  advisoryBalancesHistoricalAdapter,
  advisoryTransactionsAdapter,
  advisoryAccountsDebtAdapter,
  generateAdvisoryPositionsCacheKey,
  generateAdvisoryBalancesHistoricalCacheKey,
  generateAdvisoryTransactionsCacheKey,
  generateAdvisoryAccountsDebtCacheKey,
} from './advisory-repository-state';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

const positionsSelectors = advisoryPositionsAdapter.getSelectors();

const balancesHistoricalSelectors =
  advisoryBalancesHistoricalAdapter.getSelectors();

const transactionsSelectors = advisoryTransactionsAdapter.getSelectors();

const accountsDebtSelectors = advisoryAccountsDebtAdapter.getSelectors();

const createSelector = createTypedSelector<RootAdvisoryRepositoryState>();

export const selectAdvisoryRepositoryState = (state: RootAdvisoryRepositoryState) =>
  state.advisoryRepository;

export const selectAdvisoryPositions = createSelector(
  selectAdvisoryRepositoryState,
  ({ positions }) => positions,
);

export const selectAdvisoryBalancesHistorical = createSelector(
  selectAdvisoryRepositoryState,
  ({ balancesHistorical }) => balancesHistorical,
);

export const selectAdvisoryTransactions = createSelector(
  selectAdvisoryRepositoryState,
  ({ transactions }) => transactions,
);

export const selectAdvisoryAccountsDebt = createSelector(
  selectAdvisoryRepositoryState,
  ({ accountsDebt }) => accountsDebt,
);

export const selectAdvisoryPositionsByKey = createSelector(
  [
    selectAdvisoryPositions,
    (_state, currency: AccountCurrency, clientId: number, iban: string) =>
      generateAdvisoryPositionsCacheKey(currency, clientId, iban),
  ],
  (state, key) => positionsSelectors.selectById(state, key),
);

export const selectAdvisoryBalancesHistoricalByKey = createSelector(
  [
    selectAdvisoryBalancesHistorical,
    (
      _state,
      currency: AccountCurrency,
      clientId: number,
      iban: string,
      startDate: string,
      endDate: string,
    ) =>
      generateAdvisoryBalancesHistoricalCacheKey(
        currency,
        clientId,
        iban,
        startDate,
        endDate,
      ),
  ],
  (state, key) => balancesHistoricalSelectors.selectById(state, key),
);

export const selectAdvisoryTransactionsByKey = createSelector(
  [
    selectAdvisoryTransactions,
    (
      _state,
      currency: AccountCurrency,
      clientId: number,
      iban: string,
      startDate: string,
    ) => generateAdvisoryTransactionsCacheKey(currency, clientId, iban, startDate),
  ],
  (state, key) => transactionsSelectors.selectById(state, key),
);

export const selectAdvisoryAccountsDebtByKey = createSelector(
  [
    selectAdvisoryAccountsDebt,
    (
      _state,
      currency: AccountCurrency,
      clientId: number,
      iban: string,
      startDate: string,
      endDate: string,
    ) =>
      generateAdvisoryAccountsDebtCacheKey(
        currency,
        clientId,
        iban,
        startDate,
        endDate,
      ),
  ],
  (state, key) => accountsDebtSelectors.selectById(state, key),
);

/**
 * Select positions by a list of IBANs Returns an array of cached positions data for
 * the given IBANs
 */
export const selectAdvisoryPositionsByIbans = createSelector(
  [
    selectAdvisoryPositions,
    (_state: RootAdvisoryRepositoryState, currency: AccountCurrency) => currency,
    (_state: RootAdvisoryRepositoryState, _c: AccountCurrency, clientId: number) =>
      clientId,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      ibans: string[],
    ) => ibans,
  ],
  (state, currency, clientId, ibans) => {
    return ibans
      .map(iban => {
        const key = generateAdvisoryPositionsCacheKey(currency, clientId, iban);
        return positionsSelectors.selectById(state, key);
      })
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item))
      .map(item => item.data);
  },
);

/**
 * Select balances historical by a list of IBANs and date range Returns an array of
 * cached balances historical data for the given IBANs
 */
export const selectAdvisoryBalancesHistoricalByIbans = createSelector(
  [
    selectAdvisoryBalancesHistorical,
    (_state: RootAdvisoryRepositoryState, currency: AccountCurrency) => currency,
    (_state: RootAdvisoryRepositoryState, _c: AccountCurrency, clientId: number) =>
      clientId,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      ibans: string[],
    ) => ibans,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      _ibans: string[],
      startDate: string,
    ) => startDate,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      _ibans: string[],
      _startDate: string,
      endDate: string,
    ) => endDate,
  ],
  (state, currency, clientId, ibans, startDate, endDate) => {
    return ibans
      .map(iban => {
        const key = generateAdvisoryBalancesHistoricalCacheKey(
          currency,
          clientId,
          iban,
          startDate,
          endDate,
        );
        return balancesHistoricalSelectors.selectById(state, key);
      })
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item))
      .map(item => item.data);
  },
);

/**
 * Select transactions by a list of IBANs and start date Returns an array of cached
 * transactions data for the given IBANs
 */
export const selectAdvisoryTransactionsByIbans = createSelector(
  [
    selectAdvisoryTransactions,
    (_state: RootAdvisoryRepositoryState, currency: AccountCurrency) => currency,
    (_state: RootAdvisoryRepositoryState, _c: AccountCurrency, clientId: number) =>
      clientId,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      ibans: string[],
    ) => ibans,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      _ibans: string[],
      startDate: string,
    ) => startDate,
  ],
  (state, currency, clientId, ibans, startDate) => {
    return ibans
      .map(iban => {
        const key = generateAdvisoryTransactionsCacheKey(
          currency,
          clientId,
          iban,
          startDate,
        );
        return transactionsSelectors.selectById(state, key);
      })
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item))
      .map(item => item.data);
  },
);

/**
 * Select accounts debt by a list of IBANs and date range Returns an array of cached
 * debt account data for the given IBANs
 */
export const selectAdvisoryAccountsDebtByIbans = createSelector(
  [
    selectAdvisoryAccountsDebt,
    (_state: RootAdvisoryRepositoryState, currency: AccountCurrency) => currency,
    (_state: RootAdvisoryRepositoryState, _c: AccountCurrency, clientId: number) =>
      clientId,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      ibans: string[],
    ) => ibans,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      _ibans: string[],
      startDate: string,
    ) => startDate,
    (
      _state: RootAdvisoryRepositoryState,
      _c: AccountCurrency,
      _clientId: number,
      _ibans: string[],
      _startDate: string,
      endDate: string,
    ) => endDate,
  ],
  (state, currency, clientId, ibans, startDate, endDate) =>
    ibans
      .map(iban => {
        const key = generateAdvisoryAccountsDebtCacheKey(
          currency,
          clientId,
          iban,
          startDate,
          endDate,
        );
        return accountsDebtSelectors.selectById(state, key);
      })
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item))
      .map(item => item.data),
);

/** Select multiple positions by keys */
export const selectAdvisoryPositionsByKeys = createSelector(
  [selectAdvisoryPositions, (_state, keys: string[]) => keys],
  (state, keys) =>
    keys
      .map(key => positionsSelectors.selectById(state, key))
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item)),
);

/** Select multiple balances historical by keys */
export const selectAdvisoryBalancesHistoricalByKeys = createSelector(
  [selectAdvisoryBalancesHistorical, (_state, keys: string[]) => keys],
  (state, keys) =>
    keys
      .map(key => balancesHistoricalSelectors.selectById(state, key))
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item)),
);

/** Select multiple transactions by keys */
export const selectAdvisoryTransactionsByKeys = createSelector(
  [selectAdvisoryTransactions, (_state, keys: string[]) => keys],
  (state, keys) =>
    keys
      .map(key => transactionsSelectors.selectById(state, key))
      .filter((item): item is NonNullable<typeof item> => !isUndefined(item)),
);
